import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useTaskSelectionModalStore } from './task-selection-modal';
import { useTimemanStore } from '@/store/timeman';
import { useTaskExtensionModal } from './task-extension-modal';
import { getTask } from '@/entities/task/api/tasks';
import { pauseTaskTimer, startTaskTimer } from '@/lib/api/timeman';
import { TaskDetails } from '@/shared/types/task';
import { useAuthStore } from '@/store/auth';
import { fetchElapsedTimes } from '@/entities/task/api/elapsed-time';
import { computeElapsedSeconds } from '@/features/tasks/mobile-calendar/utils/computeElapsedSeconds';

type ActiveTimerDto = {
  userId?: number;
  duration: number;
  startTime: string;
};

type TaskKind = 'intermediate' | 'meeting' | 'task';

interface TaskTimer {
  taskId: string;
  title: string;
  plannedMinutes: number;
  plannedSeconds: number | null;
  startTime: number | null;
  totalElapsed: number;
  /** Длительность ТЕКУЩЕГО запуска (с последнего «старт», без истории по
   *  задаче) на момент lastSyncedAt — для «сколько тикает эту сессию». */
  segmentElapsedMs: number;
  isRunning: boolean;
  lastSyncedAt: number | null;
  /** Тип задачи — нужен монитору, чтобы по таймауту открыть нужную модалку
   *  (для промежуточных — выбор следующего действия с обязательным результатом). */
  kind?: TaskKind;
}

interface TaskTimerState {
  tasks: TaskTimer[];
  activeTaskId: string | null;
  lastTaskId: string | null;

  updateTaskMeta: (
    taskId: string,
    meta: { title?: string; plannedMinutes?: number; kind?: TaskKind }
  ) => void;

  startTask: (
    taskId: string,
    meta?: { title: string; plannedMinutes: number; kind?: TaskKind }
  ) => Promise<void>;

  requestPause: (taskId?: string) => Promise<void>;
  pauseTask: (taskId?: string, breakMinutes?: number) => Promise<void>;
  stopTask: (taskId: string, isOverdue?: boolean) => number;
  getCurrentElapsed: (taskId: string) => number;
  getCurrentSegmentElapsed: (taskId: string) => number;
  getTask: (taskId: string) => TaskTimer | undefined;
  getAllTasks: () => TaskTimer[];
  resetTaskTimer: (taskId: string) => void;
  resetAllTasks: () => void;
  pauseTaskLocal: (taskId?: string) => void;
  syncFromServer: (taskId: string) => Promise<void>;
}

function durationToMs(duration: number): number {
  if (!Number.isFinite(duration) || duration < 0) {
    return 0;
  }
  return Math.floor(duration * 1000);
}

function pickActiveTimer(
  activeTimers: ActiveTimerDto[] | undefined,
  currentUserId?: number
): ActiveTimerDto | null {
  if (!activeTimers || activeTimers.length === 0) {
    return null;
  }
  if (typeof currentUserId === 'number') {
    const mine = activeTimers.find((t) => t.userId === currentUserId);
    return mine ?? null;
  }
  return activeTimers[0] ?? null;
}

function upsertTimer(
  list: TaskTimer[],
  patch: Omit<TaskTimer, 'taskId'> & { taskId: string }
): TaskTimer[] {
  const idx = list.findIndex((t) => t.taskId === patch.taskId);
  if (idx === -1) {
    return [...list, patch];
  }
  const next = list.slice();
  next[idx] = { ...next[idx], ...patch };
  return next;
}

async function fetchRecordedMs(taskId: string): Promise<number> {
  try {
    const data = await fetchElapsedTimes(taskId);
    const seconds = computeElapsedSeconds(data) ?? 0;
    return Math.max(0, Math.floor(seconds * 1000));
  } catch (e) {
    console.warn('fetchRecordedMs failed', e);
    return 0;
  }
}

export const useTaskTimerStore = create<TaskTimerState>()(
  persist(
    (set, get) => ({
      tasks: [],
      activeTaskId: null,
      lastTaskId: null,

      syncFromServer: async (taskId: string) => {
        const state = get();

        const res = await getTask(taskId);
        const core = res?.core as TaskDetails | undefined;
        if (!core) {
          return;
        }

        const currentUserId = useAuthStore.getState().userId ?? undefined;
        const active = pickActiveTimer(core.activeTimers, currentUserId);
        const isRunning = Boolean(active);
        const recordedMs = await fetchRecordedMs(taskId);
        const activeMs = active ? durationToMs(active.duration) : 0;
        const totalAtSyncMs = recordedMs + (isRunning ? activeMs : 0);

        const prev = state.tasks.find((t) => t.taskId === taskId);

        const plannedSeconds =
          core.timeEstimate != null && Number(core.timeEstimate) > 0
            ? Number(core.timeEstimate)
            : null;

        const next: TaskTimer = {
          taskId,
          title: core.title ?? prev?.title ?? '',
          plannedMinutes: prev?.plannedMinutes ?? 0,
          plannedSeconds,
          startTime: null,
          totalElapsed: totalAtSyncMs,
          segmentElapsedMs: isRunning ? activeMs : 0,
          isRunning,
          lastSyncedAt: Date.now(),
          kind: prev?.kind,
        };

        const nextActiveTaskId = isRunning
          ? taskId
          : state.activeTaskId === taskId
            ? null
            : state.activeTaskId;

        set({
          tasks: upsertTimer(state.tasks, next),
          activeTaskId: nextActiveTaskId,
        });
      },

      startTask: async (
        taskId: string,
        meta?: { title: string; plannedMinutes: number; kind?: TaskKind }
      ) => {
        const state = get();

        const prevActiveId = state.activeTaskId;
        if (prevActiveId && prevActiveId !== taskId) {
          try {
            await pauseTaskTimer(prevActiveId);
          } catch (e) {
            console.warn('Не удалось поставить на паузу предыдущий таймер:', e);
          }
        }

        await startTaskTimer(taskId);
        await get().syncFromServer(taskId);

        if (meta) {
          get().updateTaskMeta(taskId, {
            title: meta.title,
            plannedMinutes: meta.plannedMinutes,
            kind: meta.kind,
          });
        }

        const timemanStore = useTimemanStore.getState();
        if (timemanStore.status === 'closed') {
          timemanStore.openWorkday().catch((error) => {
            console.error(
              'Не удалось автоматически открыть рабочий день:',
              error
            );
          });
        }

        set({ activeTaskId: taskId, lastTaskId: taskId });
      },

      requestPause: async (taskId?: string) => {
        const state = get();
        const targetTaskId = taskId || state.activeTaskId;
        if (!targetTaskId) {
          return;
        }

        set({ lastTaskId: targetTaskId });

        // Открываем модалку паузы СРАЗУ (синхронно), до await pauseTask.
        // Иначе монитор времени (useTaskTimeMonitor) успевает за время
        // ожидания API перехватить действие и показать TimeUpModal.
        useTaskSelectionModalStore
          .getState()
          .openModal({ mode: 'pause', taskId: targetTaskId });

        try {
          await get().pauseTask(targetTaskId, 0);
        } catch (e) {
          console.error('requestPause failed', e);
        }
      },

      pauseTask: async (taskId?: string, breakMinutes?: number) => {
        const state = get();
        const targetTaskId = taskId || state.activeTaskId;
        if (!targetTaskId) {
          return;
        }

        await pauseTaskTimer(targetTaskId);
        await get().syncFromServer(targetTaskId);

        set({
          activeTaskId:
            state.activeTaskId === targetTaskId ? null : state.activeTaskId,
          lastTaskId: targetTaskId,
        });

        const timemanStore = useTimemanStore.getState();
        if (typeof breakMinutes === 'number') {
          await timemanStore.pauseWorkday(breakMinutes);
        } else {
          await timemanStore.pauseWorkday(0);
        }
      },

      pauseTaskLocal: (taskId?: string) => {
        const state = get();
        const targetTaskId = String(taskId || state.activeTaskId || '');
        if (!targetTaskId) {
          return;
        }

        const updatedTasks = state.tasks.map((t) =>
          String(t.taskId) === targetTaskId
            ? {
                ...t,
                isRunning: false,
                startTime: null,
                lastSyncedAt: Date.now(),
              }
            : t
        );

        set({
          tasks: updatedTasks,
          activeTaskId:
            String(state.activeTaskId ?? '') === targetTaskId
              ? null
              : state.activeTaskId,
          lastTaskId: targetTaskId,
        });
      },

      updateTaskMeta: (taskId, meta) => {
        const state = get();
        set({
          tasks: state.tasks.map((task) =>
            task.taskId === taskId
              ? {
                  ...task,
                  title: meta.title ?? task.title,
                  plannedMinutes: meta.plannedMinutes ?? task.plannedMinutes,
                  kind: meta.kind ?? task.kind,
                }
              : task
          ),
        });
      },

      stopTask: (taskId: string, isOverdue = false) => {
        const state = get();
        const task = state.tasks.find((t) => t.taskId === taskId);
        if (!task) {
          return 0;
        }

        const finalElapsed = get().getCurrentElapsed(taskId);

        const updatedTasks = state.tasks.filter((t) => t.taskId !== taskId);

        set({
          tasks: updatedTasks,
          activeTaskId:
            state.activeTaskId === taskId ? null : state.activeTaskId,
          lastTaskId: taskId,
        });

        if (!isOverdue) {
          const extensionModal = useTaskExtensionModal.getState();
          if (!extensionModal.isOpen) {
            useTaskSelectionModalStore.getState().openModal();
          }
        }

        return Math.floor(finalElapsed / 1000);
      },

      getCurrentElapsed: (taskId: string) => {
        const state = get();
        const task = state.tasks.find((t) => t.taskId === taskId);
        if (!task) {
          return 0;
        }

        let currentElapsed = task.totalElapsed;

        if (task.isRunning && task.lastSyncedAt) {
          currentElapsed += Math.max(0, Date.now() - task.lastSyncedAt);
        }

        return currentElapsed;
      },

      getCurrentSegmentElapsed: (taskId: string) => {
        const state = get();
        const task = state.tasks.find((t) => t.taskId === taskId);
        if (!task) {
          return 0;
        }

        // ?? 0 — у записей, оставшихся в localStorage с прошлой версии
        // стора, этого поля ещё нет (появится после следующего sync).
        let currentElapsed = task.segmentElapsedMs ?? 0;

        if (task.isRunning && task.lastSyncedAt) {
          currentElapsed += Math.max(0, Date.now() - task.lastSyncedAt);
        }

        return currentElapsed;
      },

      getTask: (taskId: string) => get().tasks.find((t) => t.taskId === taskId),

      getAllTasks: () => get().tasks,

      resetTaskTimer: (taskId: string) => {
        const state = get();
        const updatedTasks = state.tasks.filter((t) => t.taskId !== taskId);

        set({
          tasks: updatedTasks,
          activeTaskId:
            state.activeTaskId === taskId ? null : state.activeTaskId,
          lastTaskId: state.activeTaskId === taskId ? null : state.lastTaskId,
        });
      },

      resetAllTasks: () => {
        set({ tasks: [], activeTaskId: null, lastTaskId: null });
      },
    }),
    { name: 'task-timer-storage' }
  )
);
